import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardService from '../services/dashboardService';
import TaskCompletionModal from '../components/TaskCompletionModal';
import SelfReportTaskModal from '../components/SelfReportTaskModal';
import VolunteerNav from '../components/VolunteerNav';

const MyTasks = () => {
  const { user, logout } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState({});

  // Task completion modal state
  const [completionModal, setCompletionModal] = useState({
    isOpen: false,
    taskId: null,
    taskTitle: '',
    loading: false
  });

  // Self-report modal state
  const [selfReportModal, setSelfReportModal] = useState({ isOpen: false, loading: false });

  // Expanded completion details
  const [expandedId, setExpandedId] = useState(null);

  // Filter and sort states
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    sortBy: 'due_date',
    sortOrder: 'asc'
  });
  
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20
  });

  // Load tasks based on current filters
  const loadTasks = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page: page,
        limit: pagination.items_per_page
      };
      
      const result = await DashboardService.getMyTasks(params);
      setTasks(result.tasks);
      setStats(result.stats);
      setPagination(result.pagination);
      setError('');
    } catch (error) {
      console.error('Load tasks error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Load tasks on component mount and filter changes
  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle task status update
  const handleStatusUpdate = async (taskId, newStatus, completionNotes = null) => {
    const updateKey = `${taskId}-${newStatus}`;

    try {
      setUpdating(prev => ({ ...prev, [updateKey]: true }));
      setCompletionModal(prev => ({ ...prev, loading: true }));

      await DashboardService.updateTaskStatus(taskId, newStatus, completionNotes);
      await loadTasks(pagination.current_page);

      // Close modal if it was a completion
      if (newStatus === 'completed') {
        setCompletionModal({
          isOpen: false,
          taskId: null,
          taskTitle: '',
          loading: false
        });
      }

    } catch (error) {
      console.error('Task update error:', error);
      setError(`Failed to update task: ${error.message}`);
      setCompletionModal(prev => ({ ...prev, loading: false }));
    } finally {
      setUpdating(prev => ({ ...prev, [updateKey]: false }));
    }
  };

  // Open completion modal
  const openCompletionModal = (taskId, taskTitle) => {
    setCompletionModal({
      isOpen: true,
      taskId,
      taskTitle,
      loading: false
    });
  };

  // Close completion modal
  const closeCompletionModal = () => {
    setCompletionModal({
      isOpen: false,
      taskId: null,
      taskTitle: '',
      loading: false
    });
  };

  // Handle completion confirmation
  const handleCompletionConfirm = (notes) => {
    if (completionModal.taskId) {
      handleStatusUpdate(completionModal.taskId, 'completed', notes);
    }
  };

  // Handle self-reported task submission
  const handleSelfReportSubmit = async (formData) => {
    try {
      setSelfReportModal((prev) => ({ ...prev, loading: true }));
      await DashboardService.logSelfReportedTask(formData);
      setSelfReportModal({ isOpen: false, loading: false });
      await loadTasks(pagination.current_page);
    } catch (err) {
      setError(`Failed to log task: ${err.message}`);
      setSelfReportModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';

    // Parse date as local time to avoid timezone shifts
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(year, month - 1, day);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Due today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Due tomorrow';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  // Get urgency styling
  const getUrgencyStyle = (task) => {
    // No color scheme for completed tasks
    if (task.assignment_status === 'completed') return '';

    if (!task.due_date) return '';

    // Parse date as local time to avoid timezone shifts
    const [year, month, day] = task.due_date.split('T')[0].split('-');
    const dueDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysDiff = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

    if (daysDiff < 0) return 'border-l-red-500 bg-red-50'; // Overdue
    if (daysDiff === 0) return 'border-l-orange-500 bg-orange-50'; // Due today
    if (daysDiff <= 3) return 'border-l-yellow-500 bg-yellow-50'; // Due soon
    return 'border-l-blue-500 bg-blue-50'; // Normal
  };

  // Get status badge styling - simplified model with overdue detection
  const getStatusStyle = (status, dueDate = null) => {
    // Check if task is overdue (due date has passed and not completed)
    let isOverdue = false;
    if (dueDate && status !== 'completed') {
      // Parse date as local time to avoid timezone shifts
      const [year, month, day] = dueDate.split('T')[0].split('-');
      const dueDateObj = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      isOverdue = dueDateObj < today;
    }

    if (isOverdue) {
      return 'bg-red-100 text-red-800';
    }

    const styles = {
      assigned: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      in_progress: 'bg-yellow-100 text-yellow-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  // Get priority styling
  const getPriorityStyle = (priority) => {
    const styles = {
      urgent: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-blue-100 text-blue-800',
      low: 'bg-green-100 text-green-800'
    };
    return styles[priority] || 'bg-gray-100 text-gray-800';
  };

  // Category display labels
  const categoryLabels = {
    general: 'General',
    training: 'Training',
    event_prep: 'Event Prep',
    administrative: 'Administrative',
    outreach: 'Outreach',
    fundraising: 'Fundraising'
  };

  if (loading && tasks.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <VolunteerNav currentPage="My Tasks" />

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pt-16 lg:pt-0 h-screen" style={{ scrollbarGutter: 'stable' }}>
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Page header with Log Task button */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center space-x-2.5 mb-1">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
              <h1 className="text-2xl font-bold text-gray-900">My Tasks</h1>
            </div>
            <p className="text-sm text-gray-500">Track and manage your assigned work</p>
          </div>
          <button
            onClick={() => setSelfReportModal({ isOpen: true, loading: false })}
            className="bg-success-600 hover:bg-success-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            + Log Completed Task
          </button>
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="p-2.5 rounded-full bg-blue-50">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-500">Total Tasks</p>
                <p className="text-xl font-semibold text-gray-900">{stats?.total_tasks || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="p-2.5 rounded-full bg-yellow-50">
                <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-500">Assigned</p>
                <p className="text-xl font-semibold text-gray-900">{stats?.assigned_tasks || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="p-2.5 rounded-full bg-green-50">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-500">Completed</p>
                <p className="text-xl font-semibold text-gray-900">{stats?.completed_tasks || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="p-2.5 rounded-full bg-red-50">
                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-500">Overdue</p>
                <p className="text-xl font-semibold text-gray-900">{stats?.overdue_tasks || 0}</p>
              </div>
            </div>
          </div>

        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            {/* Status Tabs */}
            <div className="flex items-center space-x-1">
              {[
                { id: "all", label: "All", count: stats?.total_tasks || 0 },
                { id: "assigned", label: "Active", count: stats?.assigned_tasks || 0 },
                { id: "completed", label: "Completed", count: stats?.completed_tasks || 0 },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleFilterChange('status', tab.id === filters.status ? 'all' : tab.id)}
                  className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                    filters.status === tab.id
                      ? "bg-primary-600 text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  {tab.label}
                  <span className={`ml-1.5 text-xs ${filters.status === tab.id ? "text-blue-200" : "text-gray-400"}`}>{tab.count}</span>
                </button>
              ))}
            </div>

            {/* Right side: priority + sort */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1.5">
                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
                <select
                  value={filters.priority}
                  onChange={(e) => handleFilterChange('priority', e.target.value)}
                  className="text-sm border-0 bg-transparent text-gray-600 focus:outline-none cursor-pointer font-medium"
                >
                  <option value="all">All Priority</option>
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              <div className="w-px h-5 bg-gray-200" />

              <div className="flex items-center space-x-1">
                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" /></svg>
                {[
                  { id: "due_date", label: "Due Date" },
                  { id: "priority", label: "Priority" },
                  { id: "title", label: "Name" },
                ].map((s) => (
                  <button
                    key={s.id}
                    onClick={() => {
                      if (filters.sortBy === s.id) {
                        handleFilterChange('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        handleFilterChange('sortBy', s.id);
                        handleFilterChange('sortOrder', 'asc');
                      }
                    }}
                    className={`px-2 py-1 text-xs rounded font-medium transition-colors flex items-center space-x-0.5 ${
                      filters.sortBy === s.id ? "bg-gray-100 text-gray-900" : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <span>{s.label}</span>
                    {filters.sortBy === s.id && (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={filters.sortOrder === 'asc' ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Tasks List */}
        <div className="space-y-4">
          {tasks.length > 0 ? (
            tasks.map((task) => {
              const isCompleted = task.assignment_status === 'completed';
              const isOverdue = (() => {
                if (!task.due_date || isCompleted) return false;
                const [year, month, day] = task.due_date.split('T')[0].split('-');
                const dueDate = new Date(year, month - 1, day);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return dueDate < today;
              })();
              const isExpanded = expandedId === task.id;

              return (
              <div key={task.id} className={`bg-white rounded-lg shadow transition-shadow hover:shadow-md ${getUrgencyStyle(task) ? `border-l-4 ${getUrgencyStyle(task)}` : ''} ${isCompleted ? 'opacity-80' : ''}`}>
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {/* Task Header */}
                      <div className="mb-3">
                        <h3 className={`text-lg font-semibold mb-2 ${isCompleted ? 'text-gray-500' : 'text-gray-900'}`}>
                          {task.title}
                        </h3>
                        <div className="flex items-center flex-wrap gap-2">
                          <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusStyle(task.assignment_status, task.due_date)}`}>
                            {isOverdue ? 'overdue' : task.assignment_status.replace('_', ' ')}
                          </span>
                          <span className={`px-3 py-1 text-xs font-medium rounded-full ${getPriorityStyle(task.priority)}`}>
                            {task.priority}
                          </span>
                          {task.category && (
                            <span className="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                              {categoryLabels[task.category] || task.category}
                            </span>
                          )}
                          {task.self_reported && (
                            <span className="px-3 py-1 text-xs font-medium rounded-full bg-teal-100 text-teal-800">
                              Self-Reported
                            </span>
                          )}
                          {isOverdue && (
                            <span className="flex items-center space-x-1 text-xs text-red-600 font-medium">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              <span>Overdue</span>
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Task Description */}
                      {task.description && (
                        <p className="text-gray-700 mb-4">{task.description}</p>
                      )}

                      {/* Task Metadata */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-4">
                        <div>
                          <span className="font-medium">Due Date:</span>
                          <div className={`mt-1 ${task.urgency_level <= 0 ? 'text-red-600 font-medium' : task.urgency_level <= 2 ? 'text-orange-600' : ''}`}>
                            {formatDate(task.due_date)}
                          </div>
                        </div>
                        
                        {task.estimated_hours && (
                          <div>
                            <span className="font-medium">Estimated Time:</span>
                            <div className="mt-1">{task.estimated_hours} hours</div>
                          </div>
                        )}
                        
                        {task.assigned_by_name && !task.self_reported && (
                          <div>
                            <span className="font-medium">Assigned By:</span>
                            <div className="mt-1">{task.assigned_by_name} {task.assigned_by_lastname}</div>
                          </div>
                        )}
                        
                        <div>
                          <span className="font-medium">Assigned:</span>
                          <div className="mt-1">{new Date(task.assignment_date).toLocaleDateString()}</div>
                        </div>
                      </div>

                      {/* Completion Details */}
                      {isCompleted && (
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : task.id)}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium mb-1"
                        >
                          {isExpanded ? 'Hide details' : 'View completion details'}
                        </button>
                      )}

                      {isCompleted && isExpanded && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 mt-2">
                          <div className="text-sm">
                            <div className="font-medium text-green-800 mb-2">
                              Completed on {new Date(task.completed_at).toLocaleDateString()}
                            </div>
                            {task.completion_notes && (
                              <div>
                                <span className="font-medium text-green-800">Your notes:</span>
                                <p className="text-green-700 mt-1">{task.completion_notes}</p>
                              </div>
                            )}
                            {task.admin_feedback && (
                              <div className="mt-3 pt-3 border-t border-green-300">
                                <span className="font-medium text-green-800">Admin feedback:</span>
                                <p className="text-green-700 mt-1">{task.admin_feedback}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons - Simplified Model */}
                    <div className="ml-6 flex flex-col space-y-2">
                      {task.assignment_status === 'assigned' && (
                        <button
                          onClick={() => openCompletionModal(task.id, task.title)}
                          disabled={updating[`${task.id}-completed`]}
                          className="inline-flex items-center space-x-1.5 bg-success-600 hover:bg-success-700 disabled:bg-success-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
                        >
                          {updating[`${task.id}-completed`] ? (
                            <span>Completing...</span>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                              <span>Complete</span>
                            </>
                          )}
                        </button>
                      )}

                      {isCompleted && (
                        <div className="inline-flex items-center space-x-1.5 text-green-600 text-sm font-medium">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          <span>Done</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              );
            })
          ) : (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
              <p className="text-gray-600">
                {Object.values(filters).some(v => v !== 'all')
                  ? 'Try adjusting your filters to see more tasks'
                  : 'No tasks have been assigned to you yet'
                }
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="mt-8 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.current_page - 1) * pagination.items_per_page) + 1} to{' '}
              {Math.min(pagination.current_page * pagination.items_per_page, pagination.total_items)} of{' '}
              {pagination.total_items} tasks
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => loadTasks(pagination.current_page - 1)}
                disabled={pagination.current_page === 1 || loading}
                className="bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded-md text-sm disabled:opacity-50"
              >
                Previous
              </button>
              
              <span className="bg-primary-600 text-white px-3 py-2 rounded-md text-sm">
                {pagination.current_page} of {pagination.total_pages}
              </span>
              
              <button
                onClick={() => loadTasks(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.total_pages || loading}
                className="bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded-md text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Task Completion Modal */}
        <TaskCompletionModal
          isOpen={completionModal.isOpen}
          onClose={closeCompletionModal}
          onConfirm={handleCompletionConfirm}
          taskTitle={completionModal.taskTitle}
          loading={completionModal.loading}
        />

        {/* Self-Report Task Modal */}
        <SelfReportTaskModal
          isOpen={selfReportModal.isOpen}
          onClose={() => setSelfReportModal({ isOpen: false, loading: false })}
          onSubmit={handleSelfReportSubmit}
          loading={selfReportModal.loading}
        />
        </div>
      </main>
    </div>
  );
};

export default MyTasks;